import { useCallback, useMemo } from 'react';
import { useAuthenticatedFetchWithWorkspace } from '../Keycloak/useAuthenticatedFetchWithWorkspace';
import { QuerySource } from '../../types/QueryBuilder';
import { useCachedQuery } from './useCachedQuery';

export interface SavedQuery {
    QUERY_ID: string;
    USER_ID: string;
    APPLICATION_DATE: string;
    QUERY_TYPE: string;
    EXECUTION_DATE: string | null;
    DESCRIPTION: string;
    SOURCE: string;
    UPDATE_DATE: string;
    STATUS_ID: string;
    LIMIT: string;
    IS_FROM_COMPANY: string;
    GROUP_QUERY_ID: string;
    QUERY_GROUP_NAME: string;
    COMMENT_QUERY: string | null;
    QUERY_NAME: string;
}

interface SavedQueriesResponse {
    totalItems?: number;
    member?: SavedQuery[];
}

interface SavedQueriesPage {
    queries: SavedQuery[];
    totalItems: number;
}

interface UseSavedQueriesOptions {
    page?: number;
    itemsPerPage?: number;
    groupQueryIds?: string;
    isFromCompany?: 0 | 1;
    search?: string;
    source?: QuerySource;
}

const EMPTY_PAGE: SavedQueriesPage = { queries: [], totalItems: 0 };

export const useSavedQueries = ({ page, itemsPerPage, groupQueryIds, isFromCompany, search, source }: UseSavedQueriesOptions = {}) => {
    const { authFetch, workspaceId } = useAuthenticatedFetchWithWorkspace();
    const trimmedSearch = search?.trim();

    const queryString = useMemo(() => {
        const params: Record<string, string | number | undefined> = {
            page: page || undefined,
            itemsPerPage: itemsPerPage || undefined,
            groupQueryIds: groupQueryIds || undefined,
            isFromCompany,
            search: trimmedSearch || undefined,
            source,
        };
        return Object.entries(params)
            .filter(([, value]) => value !== undefined)
            .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
            .join('&');
    }, [page, itemsPerPage, groupQueryIds, isFromCompany, trimmedSearch, source]);

    const fetchQueries = useCallback(async (): Promise<SavedQueriesPage> => {
        const response: SavedQueriesResponse = await authFetch(`/getQueriesForUser${queryString ? `?${queryString}` : ''}`, { method: 'GET' });
        return { queries: response.member || [], totalItems: response.totalItems || 0 };
    }, [authFetch, queryString]);

    const { data, loading, refetch } = useCachedQuery(
        `saved_queries|${workspaceId ?? ''}|${queryString}`,
        fetchQueries,
        EMPTY_PAGE,
        'Erreur de récupération',
    );

    return { queries: data.queries, totalItems: data.totalItems, loading, refetch };
};
